import type { DexEntry, DexStatus, PokedexView } from '../../shared/protocol.ts';
import { $, $$, el } from '../common/dom.ts';
import { icon, withIcon } from '../common/icons.ts';
import { typeBadges } from '../common/element-badge.ts';
import { rarityBadge } from '../common/rarity-badge.ts';
import { creatureText } from '../../shared/game-config.ts';

type Filter = 'all' | DexStatus;

const STATUS_LABELS: Record<DexStatus, string> = { caught: 'Capturé', seen: 'Vu', unknown: 'Inconnu' };
const dateFormat = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

const dexNumber = (entry: DexEntry) => `#${String(entry.number).padStart(3, '0')}`;

/** Image de l'espèce ; inconnue, un simple point d'interrogation (le serveur n'en envoie pas l'image). */
const dexImage = (entry: DexEntry, lazy = false): HTMLElement => (entry.image
  ? el('img', { src: entry.image, alt: lazy ? '' : (entry.name ?? ''), loading: lazy ? 'lazy' : 'eager' })
  : el('span', { className: 'dex-mystery', textContent: '?', title: 'Espèce inconnue' }));

/** Pokédex : toutes les espèces du jeu, révélées au fil des rencontres et des captures. */
export class PokedexPanel {
  private readonly overlay = $('#pokedex');
  private readonly grid = $('.dex-grid', this.overlay);
  private readonly detail = $('.dex-detail', this.overlay);
  private readonly load: () => Promise<PokedexView>;
  private view: PokedexView | null = null;
  private filter: Filter = 'all';
  private selectedId: number | null = null;
  /** Chargements lancés : seule la réponse du dernier est affichée. */
  private loads = 0;

  constructor(load: () => Promise<PokedexView>) {
    this.load = load;
    $('#btn-dex').addEventListener('click', () => this.toggle());
    $('#dex-close').addEventListener('click', () => this.close());
    // Un clic sur le fond (hors de la fenêtre) ferme le Pokédex.
    this.overlay.addEventListener('click', (event) => { if (event.target === this.overlay) this.close(); });
    $('.dex-filters', this.overlay).addEventListener('click', (event) => {
      const filter = (event.target as Element).closest<HTMLButtonElement>('[data-filter]')?.dataset.filter;
      if (filter) this.setFilter(filter as Filter);
    });
  }

  get isOpen(): boolean {
    return !this.overlay.hidden;
  }

  toggle(): void {
    if (this.isOpen) this.close(); else void this.open();
  }

  close(): void {
    this.overlay.hidden = true;
    this.overlay.classList.remove('show-detail');
  }

  async open(): Promise<void> {
    this.overlay.hidden = false;
    // Seule la réponse du dernier chargement compte, et seulement si le dex est resté ouvert.
    const load = ++this.loads;
    this.grid.replaceChildren(el('p', { className: 'empty', textContent: 'Chargement…' }));
    let view: PokedexView;
    try {
      view = await this.load();
    } catch {
      if (load !== this.loads || !this.isOpen) return;
      this.grid.replaceChildren(el('p', { className: 'empty', textContent: creatureText('Impossible de charger {le dex}.') }));
      return;
    }
    if (load !== this.loads || !this.isOpen) return;
    this.view = view;
    const { entries } = view;
    if (!entries.some((entry) => entry.speciesId === this.selectedId)) {
      this.selectedId = (entries.find((entry) => entry.status !== 'unknown') ?? entries[0])?.speciesId ?? null;
    }
    this.render();
  }

  private setFilter(filter: Filter): void {
    this.filter = filter;
    this.render();
  }

  private render(): void {
    const { view } = this;
    if (!view) return;
    this.renderProgress(view);
    for (const button of $$<HTMLButtonElement>('[data-filter]', this.overlay)) button.classList.toggle('active', button.dataset.filter === this.filter);

    const visible = view.entries.filter((entry) => this.filter === 'all' || entry.status === this.filter);
    this.grid.replaceChildren(...(visible.length
      ? visible.map((entry) => this.card(entry))
      : [el('p', { className: 'empty', textContent: 'Aucune espèce dans cette catégorie.' })]));

    const selected = view.entries.find((entry) => entry.speciesId === this.selectedId);
    this.detail.replaceChildren(...(selected ? this.detailContent(selected) : []));
  }

  private renderProgress({ entries, seen, caught }: PokedexView): void {
    const total = entries.length || 1;
    $('.dex-progress-text', this.overlay).textContent = `${caught} capturés · ${seen} vus · ${entries.length} espèces`;
    $('.dex-progress .seen', this.overlay).style.width = `${(100 * seen) / total}%`;
    $('.dex-progress .caught', this.overlay).style.width = `${(100 * caught) / total}%`;
  }

  private card(entry: DexEntry): HTMLButtonElement {
    const card = el('button', { className: `dex-card ${entry.status} ${entry.speciesId === this.selectedId ? 'active' : ''}`, title: entry.name ?? '???' },
      el('span', { className: 'dex-number', textContent: dexNumber(entry) }),
      dexImage(entry, true),
      el('span', { className: 'dex-name', textContent: entry.name ?? '???' }));
    if (entry.status === 'caught') card.append(el('span', { className: 'dex-caught', title: 'Capturé' }, icon('capture')));
    card.addEventListener('click', () => {
      this.selectedId = entry.speciesId;
      this.overlay.classList.add('show-detail'); // sur mobile, la fiche s'ouvre en plein écran
      this.render();
    });
    return card;
  }

  private detailContent(entry: DexEntry): Node[] {
    const header = el('div', { className: 'dex-detail-head' },
      el('span', { className: 'dex-number', textContent: dexNumber(entry) }),
      el('span', { className: `dex-status ${entry.status}`, textContent: STATUS_LABELS[entry.status] }));

    const back = el('button', { className: 'dex-back secondary' }, ...withIcon('chevron-left', 'Retour à la liste'));
    back.addEventListener('click', () => this.overlay.classList.remove('show-detail'));
    const nodes: Node[] = [
      back,
      header,
      el('div', { className: `dex-portrait ${entry.status}` }, dexImage(entry)),
      el('h3', { textContent: entry.name ?? '???' }),
    ];
    const badges: HTMLElement[] = [];
    if (entry.element) badges.push(...typeBadges({ element: entry.element, secondElement: entry.secondElement }));
    if (entry.rarity) badges.push(rarityBadge(entry.rarity));
    if (badges.length) nodes.push(el('div', { className: 'badges' }, ...badges));
    nodes.push(el('p', { className: 'dex-history', textContent: this.history(entry) }));

    if (entry.status === 'unknown') {
      nodes.push(el('p', { className: 'dex-hint', textContent: 'Explorez les zones de farm pour le rencontrer.' }));
      return nodes;
    }
    nodes.push(el('p', {
      className: entry.description ? 'dex-description' : 'dex-hint',
      textContent: creatureText(entry.description ?? '') || (entry.status === 'caught' ? 'Aucune description.' : creatureText('Capturez-{le} pour en apprendre davantage.')),
    }));
    if (entry.evolution) {
      nodes.push(el('h4', { textContent: 'Évolution' }));
      nodes.push(el('p', { className: 'dex-description', textContent: `Évolue en ${entry.evolution.into ?? '???'} au niveau ${entry.evolution.level}.` }));
    }
    nodes.push(el('h4', { textContent: 'Où le trouver' }));
    nodes.push(entry.habitats.length
      ? el('ul', { className: 'dex-habitats' }, ...entry.habitats.map((habitat) => el('li', { textContent: habitat })))
      : el('p', { className: 'dex-hint', textContent: entry.evolution || entry.status === 'caught' ? 'Introuvable à l\'état sauvage : il s\'obtient peut-être par évolution…' : 'On ne le trouve plus à l\'état sauvage.' }));
    return nodes;
  }

  private history(entry: DexEntry): string {
    if (entry.firstCaughtAt !== null) {
      const owned = entry.owned ? ` · ${entry.owned} dans votre collection` : ' · aucun dans votre collection';
      return `Capturé pour la première fois le ${dateFormat.format(entry.firstCaughtAt)}${owned}`;
    }
    if (entry.firstSeenAt !== null) return `Vu le ${dateFormat.format(entry.firstSeenAt)} — pas encore capturé`;
    return 'Jamais rencontré';
  }
}
